import { PersonaEditorFields } from '@/components/admin/personas/persona-editor-fields';
import type { VersionedFieldsBinding } from '@/components/admin/versioned/versioned-editor';
import { useAdminSession } from '@/lib/admin-session';
import { createPersonaTone } from '@/lib/api';
import type { PersonaDraft } from '@/lib/persona-draft';
import { personaReferenceMessage } from '@/lib/persona-labels';
import type {
  InvolvementLevel,
  PersonaToneEntry,
  PersonaToneList,
  PersonaToneWrite,
} from '@matter_extractor/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The persona's form as the VERSIONED EDITOR mounts it (refonte modulaire §4,
 * §10).
 *
 * Two jobs, and neither belongs in `persona-editor-fields.tsx`:
 *
 *   - it maps the layer's binding - « here is the draft, hand me the next
 *     one » - onto one callback per field, which is the mapping the criterion's
 *     editor writes inline because a criterion has three fields and a persona
 *     has eight;
 *   - it owns the WRITE behind « ajouter un ton »: a mutation, its refusal, and
 *     the selection of the tone it just created.
 *
 * It is a component and not a callback because of that mutation: hooks cannot
 * run inside `renderFields`, which the layer calls during its own render. And
 * it is a file of its own so that `PersonaEditorFields` stays AUTONOMOUS - §13's
 * guided form mounts the fields and supplies its own creation, rather than
 * dragging this editor's cache keys along with them.
 */

export interface PersonaFormProps {
  /** What `VersionedEditor` hands its `renderFields` slot. */
  binding: VersionedFieldsBinding<PersonaDraft>;
  /** The scale of §11.5, in order. */
  levels: readonly InvolvementLevel[];
  /** The tone library of §11.5, already sorted by name by the server. */
  tones: readonly PersonaToneEntry[];
  /** Has that library actually been read? An empty list is otherwise ambiguous. */
  libraryRead: boolean;
}

export function PersonaForm({ binding, levels, tones, libraryRead }: PersonaFormProps) {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();
  const [creatingTone, setCreatingTone] = useState(false);
  const { draft, onDraft } = binding;

  const createTone = useMutation({
    mutationFn: (input: PersonaToneWrite) => createPersonaTone(input),
    onSuccess: (tone) => {
      setCreatingTone(false);
      /*
        The cache is WRITTEN and not only invalidated, and that is not an
        optimisation: the line below selects the new tone in the draft, and
        between here and the refetch landing the library would not hold it -
        so the editor would show the tone somebody just created as « ne fait
        plus partie de la bibliothèque », dashed and apart. The invalidation
        stays, to pick up whatever else moved.
      */
      queryClient.setQueryData<PersonaToneList>(['persona-tones', email], (current) =>
        current === undefined
          ? current
          : {
              tones: [...current.tones, tone].sort((a, b) => a.name.localeCompare(b.name, 'fr')),
            },
      );
      void queryClient.invalidateQueries({ queryKey: ['persona-tones', email] });
      // « Le ton entre dans la bibliothèque ET se sélectionne » (§10.1). The
      // draft keeps a COPY, never a reference: no provenance is kept at all.
      onDraft({ ...draft, tone: { name: tone.name, text: tone.text } });
    },
  });

  /** The cran the slider sits on right now, or null when it left the scale. */
  const level = levels.find((one) => one.id === draft.involvement.levelId) ?? null;

  return (
    <PersonaEditorFields
      draft={draft}
      levels={levels}
      tones={tones}
      libraryRead={libraryRead}
      readOnly={binding.readOnly}
      nameError={binding.nameError}
      onName={(name) => onDraft({ ...draft, name })}
      onNameCommit={binding.onNameCommit}
      onDirection={(direction) => onDraft({ ...draft, direction })}
      /*
        The kind changes and the two texts STAY (§10.1): « repasser en anonyme
        les cache sans les effacer du brouillon, pour qu'un aller-retour ne
        coûte pas une ressaisie ». That is the whole reason the draft's
        presentation is a flat object rather than the version's union.
      */
      onPresentationKind={(kind) =>
        onDraft({ ...draft, presentation: { ...draft.presentation, kind } })
      }
      onFirstName={(firstName) =>
        onDraft({ ...draft, presentation: { ...draft.presentation, firstName } })
      }
      onSelfDirection={(direction) =>
        onDraft({ ...draft, presentation: { ...draft.presentation, direction } })
      }
      /*
        Only the identifier moves. The name and the direction beside it are the
        FROZEN copy, and they are read again only when the cran has left the
        scale - `resolvedInvolvement` takes the live wording whenever there is
        one. A redefinition in place SURVIVES the move, because it is this
        persona's own text and the trash can is how one gives it up.
      */
      onLevel={(levelId) => onDraft({ ...draft, involvement: { ...draft.involvement, levelId } })}
      onRedefine={(prefill) =>
        onDraft({
          ...draft,
          involvement: { ...draft.involvement, overridden: true, direction: prefill },
        })
      }
      onOverrideDirection={(direction) =>
        onDraft({ ...draft, involvement: { ...draft.involvement, direction } })
      }
      /*
        The trash can: `overridden` goes back to false, and the text follows the
        cran again. The field is refilled with the cran's CURRENT wording rather
        than emptied, so the draft on screen is the one that would be saved.
      */
      onDropOverride={() =>
        onDraft({
          ...draft,
          involvement: {
            ...draft.involvement,
            overridden: false,
            direction: level?.direction ?? draft.involvement.direction,
          },
        })
      }
      onBackground={(background) => onDraft({ ...draft, background })}
      onTone={(tone) => onDraft({ ...draft, tone })}
      toneCreation={{
        open: creatingTone,
        busy: createTone.isPending,
        // H16: a taken name is refused BY NAMING the other tone. The sentence is
        // the settings tab's, worded from the `code` and never from the
        // server's English message.
        error: createTone.isError
          ? (personaReferenceMessage(createTone.error) ??
            'Le ton n’a pas pu être ajouté. Réessayez dans un instant.')
          : null,
        onOpen: () => {
          createTone.reset();
          setCreatingTone(true);
        },
        onCancel: () => {
          createTone.reset();
          setCreatingTone(false);
        },
        onCreate: (input) => createTone.mutate(input),
      }}
    />
  );
}
